import React from 'react';
import { Box, createStyles, makeStyles } from '@material-ui/core';
import {
	ExtendedTheme,
	InlineText,
	Link,
	Skeletons,
	useTheme,
	TextBlock,
	TextLabel
} from 'neon-design-system';
import PublishedBy, { PublishedByProps } from '../../../components/ui/PublishedBy';
import { ViewTracker } from '../../ui/ViewTracker';
import { Channel } from '../../../shared/types';
import Card from '../../ui/card/Card';

const useStyles = makeStyles<ExtendedTheme, ArticlePreviewCardProps>(theme => createStyles({
	container: {
		padding: theme.spacing(2),
		height: '100%',
		[theme.breakpoints.up('md')]: {
			padding: theme.spacing(3)
		},
		display: 'flex',
		flexDirection: 'column'
	},
	link: {
		...theme.typography.h5,
		color: theme.palette.grey[200],
		'&:hover': {
			color: theme.palette.primary.contrastText
		},
		[theme.breakpoints.up('md')]: {
			...theme.typography.h4
		}
	},
	summary: {
		display: 'none',
		marginTop: theme.spacing(0),
		[theme.breakpoints.up('md')]: {
			display: 'block',
			marginTop: theme.spacing(2)
		}
	},
	article: {
		borderBottom: `1px solid ${theme.palette.grey[600]}`
	},
	pinnedArticle: {
		borderBottom: 'none',
		background: theme.palette.grey[800],
		borderRadius: 4
	},
	summaryNew: {
		display: 'none',
		[theme.breakpoints.up('md')]: {
			display: 'block',
			marginBottom: theme.spacing(1)
		}
	},
	label: {
		backgroundColor: 'transparent'
	}
}));

export interface ArticlePreviewCardProps {
	title: string;
	publishedData: PublishedByProps;
	articleLink: string;
	summary?: string;
	source: string;
	sourceId?: string;
	sourceDetail?: string;
	channel?: Channel;
}

export interface PinnableArticlePreviewCardProps extends ArticlePreviewCardProps {
	isPinned?: boolean;
}

const ArticlePreviewCard = (props: ArticlePreviewCardProps) => {
	const classes = useStyles(props);
	const { title, publishedData, articleLink, source, sourceId, sourceDetail, channel } = props;

	return (
		<Card className={[classes.container, 'article-preview-card'].join(' ')}>
			{/* for now we have Articles without Channels */}
			{
				channel &&(
					<Box mb={1}>
						<TextBlock styleName="footnote" color="primary" shade={300}>
							<b>{channel.name}</b>
						</TextBlock>
					</Box>
				)
			}
			<Box mb={1}>
				<Link to={articleLink} className={classes.link}>
					<ViewTracker source={source} sourceId={sourceId} sourceDetail={sourceDetail}>
						{title}
					</ViewTracker>
				</Link>
			</Box>
			<PublishedBy
				date={publishedData.date}
				publisher={publishedData.publisher}
				momentInTimeFormat={true}
				showEditDate={false}
				editDate={publishedData.editDate}
				simplified
			/>
			{props?.summary ?
				<InlineText
					className={classes.summary}
					styleName="body"
					color="grey"
					shade={300}>
					{props.summary}
				</InlineText> : null
			}
		</Card>
	);
};

export const PinnableArticlePreviewCard = (props: PinnableArticlePreviewCardProps) => {
	const classes = useStyles(props);
	const { title, publishedData, articleLink, source, sourceId, sourceDetail, channel, isPinned } = props;

	return (
		<Box className={[classes.container, classes.article, isPinned && classes.pinnedArticle, 'article-preview-card'].filter(Boolean).join(' ')}>
			{/* for now we have Articles without Channels */}
			{
				isPinned || channel ? (
					<Box mb={1} display="flex">
						{
							isPinned ? (
								<Box mr={1}>
									<TextLabel label="Pinned" className={classes.label} />
								</Box>
							) : <></>
						}
						{
							channel ? (
								<Box>
									<TextBlock styleName="footnote" color="primary" shade={300}>
										<b>{channel.name}</b>
									</TextBlock>
								</Box>
							) : <></>
						}
					</Box>
				) : <></>
			}

			<Box mb={1}>
				<Link to={articleLink} className={classes.link}>
					<ViewTracker source={source} sourceId={sourceId} sourceDetail={sourceDetail}>
						{title}
					</ViewTracker>
				</Link>
			</Box>
			{props?.summary ?
				<InlineText
					className={classes.summaryNew}
					styleName="body"
					color="grey"
					shade={300}>
					{props.summary}
				</InlineText> : null
			}
			<PublishedBy
				date={publishedData.date}
				publisher={publishedData.publisher}
				momentInTimeFormat={true}
				showEditDate={false}
				editDate={publishedData.editDate}
				simplified
			/>
		</Box>
	);

};

export const ArticlePreviewCardSkeleton = (props: { dataLoaded?: boolean }) => {
	const classes = useStyles({} as any);
	const theme = useTheme();
	const typography = theme.theme.customTypography;

	return (
		<Card className={classes.container}>
			<Box mb={1}>
				<Skeletons count={1} height={parseInt(typography.caption2.medium.fontSize)} width="30%" dataLoaded={props.dataLoaded || false} />
			</Box>
			<Box mb={1}>
				<Skeletons count={1} height={parseInt(typography.headline1.medium.fontSize)} dataLoaded={props.dataLoaded || false} />
			</Box>
			<Skeletons count={1} height={parseInt(typography.body.medium.fontSize)} dataLoaded={props.dataLoaded || false} />
		</Card>
	)
}

export default ArticlePreviewCard;
